import React, { Component } from 'react';
import { SealCheck, LinkedinLogo, FacebookLogo, EnvelopeSimple, IdentificationCard, HouseLine, Buildings, House, Clock, WarningCircle, XCircle } from '@phosphor-icons/react';
import { cn, toVerificationState, type VerificationState } from '../../lib/utils';
// Inline globe SVG — avoids risky phosphor Globe import.
function GlobeIcon({
  size = 24,
  className,
  weight: _weight
}: {
  size?: number;
  className?: string;
  weight?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" data-id="element-379">
      <circle cx="12" cy="12" r="9" data-id="element-380" />
      <path d="M3 12h18" data-id="element-381" />
      <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0 -18" data-id="element-382" />
    </svg>;
}
export interface LandlordIdentityData {
  name: string;
  avatar?: string;
  subRole: 'proprietar' | 'agent' | null;
  agencyName?: string;
  memberSince: number;
  linkedinUrl?: string;
  facebookUrl?: string;
  websiteUrl?: string;
  bio?: string;
  verifications: {
    email: boolean | VerificationState;
    identity: boolean | VerificationState;
    business: boolean | VerificationState;
  };
  stats: {
    oferteTrimise: number;
    inchirieriDeSuccess: number;
  };
}
export interface LandlordIdentityCardProps {
  data: LandlordIdentityData;
  mode: 'owner' | 'public';
  onOpenEditProfile?: (section?: 'social' | 'bio') => void;
  onOpenVerification?: (type: 'email' | 'identity' | 'business') => void;
}
export function LandlordIdentityCard({
  data,
  mode,
  onOpenEditProfile,
  onOpenVerification
}: LandlordIdentityCardProps) {
  const fullyVerified = data.verifications.email && data.verifications.identity && data.verifications.business;
  const isAgent = data.subRole === 'agent';
  const linkedinConnected = !!data.linkedinUrl;
  const facebookConnected = !!data.facebookUrl;
  const websiteConnected = !!data.websiteUrl;
  const firstInitial = data.name?.[0]?.toUpperCase() || 'P';
  // Sub-line label: agency name replaces "Agent imobiliar" entirely when set
  const subLineLabel = isAgent ? data.agencyName?.trim() ? data.agencyName : 'Agent imobiliar' : 'Proprietar';
  const SubLineIcon = isAgent ? Buildings : House;
  // Third badge varies by sub-role
  const businessBadgeLabel = isAgent ? 'Agenție verificată' : 'Proprietate verificată';
  const businessBadgeUnverifiedLabel = isAgent ? 'Verifică agenția' : 'Verifică proprietatea';
  const BusinessIcon = isAgent ? Buildings : HouseLine;
  // Decide whether to render any social row at all.
  const hasAnySocialOwner = mode === 'owner'; // always render slots in owner mode
  const hasAnySocialPublic = mode === 'public' && (linkedinConnected || facebookConnected || websiteConnected);
  const showSocialRow = hasAnySocialOwner || hasAnySocialPublic;
  return <article className="rounded-2xl border border-dark-4 overflow-hidden bg-dark-2 p-6 sm:p-8" data-id="element-383">
      {/* Top header row */}
      <div className="relative flex flex-col sm:flex-row gap-6 items-start mb-6" data-id="element-384">
        <div className="shrink-0" data-id="element-385">
          {data.avatar ? <img src={data.avatar} alt={data.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-dark-1" data-id="element-386" /> : <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-dark-3 flex items-center justify-center text-2xl font-medium text-light-2 border-4 border-dark-1" data-id="element-387">
              {firstInitial}
            </div>}
        </div>

        <div className="flex-1 min-w-0 w-full pt-1" data-id="element-388">
          <div className="flex items-center gap-2 mb-1" data-id="element-389">
            <h1 className="text-2xl sm:text-3xl font-semibold text-light-1 truncate min-w-0" data-id="element-390">
              {data.name}
            </h1>
            {fullyVerified && <SealCheck size={24} weight="fill" className="text-lime-1 shrink-0" aria-label="Cont verificat complet" data-id="element-391" />}
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm sm:text-base font-normal text-light-3" data-id="element-392">
            <span className="inline-flex items-center gap-1.5 min-w-0" data-id="element-393">
              <SubLineIcon size={14} weight="fill" className="text-light-3 shrink-0" data-id="element-394" />
              <span className="truncate" data-id="element-395">{subLineLabel}</span>
            </span>
            <span className="text-light-4" data-id="element-396">·</span>
            <span className="whitespace-nowrap" data-id="element-397">
              Pe Izio din {data.memberSince}
            </span>
          </div>
        </div>

        {/* Social icons — always top-right (same pattern as tenant IdentityCard) */}
        {showSocialRow && <div className="absolute top-0 right-0 flex items-center gap-3" data-id="element-398">
            {renderSocialIcons({
          mode,
          data,
          linkedinConnected,
          facebookConnected,
          websiteConnected,
          onOpenEditProfile
        })}
          </div>}
      </div>

      {/* Trust badges row */}
      <div id={mode === 'owner' ? 'profile-verifications' : undefined} className={cn('flex flex-wrap gap-3', mode === 'owner' && 'scroll-mt-24')} data-id="element-399">
        <TrustBadge Icon={EnvelopeSimple} state={toVerificationState(data.verifications.email)} label="Email" actionLabel="Verifică email" mode={mode} onClick={() => onOpenVerification?.('email')} data-id="element-400" />
        <TrustBadge Icon={IdentificationCard} state={toVerificationState(data.verifications.identity)} label="ID" actionLabel="Verifică identitate" mode={mode} onClick={() => onOpenVerification?.('identity')} data-id="element-401" />
        <TrustBadge Icon={BusinessIcon} state={toVerificationState(data.verifications.business)} label={isAgent ? 'Agenție' : 'Proprietate'} actionLabel={businessBadgeUnverifiedLabel} mode={mode} onClick={() => onOpenVerification?.('business')} data-id="element-402" />
      </div>

      <div className="border-t border-dark-4 my-6" data-id="element-403"></div>

      {/* Despre mine */}
      {data.bio ? <div data-id="element-404">
          <h2 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-405">
            {isAgent ? 'Despre mine / agenție' : 'Despre mine'}
          </h2>
          <p className="text-sm font-normal text-light-2 leading-relaxed" data-id="element-406">
            {data.bio}
          </p>
        </div> : mode === 'owner' ? <button onClick={() => onOpenEditProfile?.('bio')} className="w-full text-left p-3 rounded-xl border border-dashed border-dark-4 text-sm text-light-3 hover:text-light-1 hover:border-light-4 transition-colors cursor-pointer" data-id="element-407">
          + Adaugă o descriere despre {isAgent ? 'tine / agenție' : 'tine'}
        </button> : null}

      {/* Stats row */}
      <div className="mt-6 pt-6 border-t border-dark-4 text-sm text-light-3" data-id="element-408">
        {mode === 'owner' ? <>
            {data.stats.oferteTrimise}{' '}
            {data.stats.oferteTrimise === 1 ? 'ofertă trimisă' : 'oferte trimise'}{' '}
            · {data.stats.inchirieriDeSuccess}{' '}
            {data.stats.inchirieriDeSuccess === 1 ? 'închiriere de succes' : 'închirieri de succes'}
          </> : <>
            {data.stats.inchirieriDeSuccess}{' '}
            {data.stats.inchirieriDeSuccess === 1 ? 'închiriere de succes' : 'închirieri de succes'}
          </>}
      </div>
    </article>;
}
// Helper to render social icons in either layout slot
function renderSocialIcons({
  mode,
  data,
  linkedinConnected,
  facebookConnected,
  websiteConnected,
  onOpenEditProfile
}: {
  mode: 'owner' | 'public';
  data: LandlordIdentityData;
  linkedinConnected: boolean;
  facebookConnected: boolean;
  websiteConnected: boolean;
  onOpenEditProfile?: (section?: 'social' | 'bio') => void;
}) {
  if (mode === 'owner') {
    return <>
        <SocialIconButton Icon={LinkedinLogo} connected={linkedinConnected} url={data.linkedinUrl} connectedLabel="LinkedIn conectat" disconnectedLabel="Conectează LinkedIn" onConnect={() => onOpenEditProfile?.('social')} data-id="element-409" />
        <SocialIconButton Icon={FacebookLogo} connected={facebookConnected} url={data.facebookUrl} connectedLabel="Facebook conectat" disconnectedLabel="Conectează Facebook" onConnect={() => onOpenEditProfile?.('social')} data-id="element-410" />
        <SocialIconButton Icon={GlobeIcon} connected={websiteConnected} url={data.websiteUrl} connectedLabel="Website conectat" disconnectedLabel="Adaugă website" onConnect={() => onOpenEditProfile?.('social')} data-id="element-411" />
      </>;
  }
  return <>
      {linkedinConnected && <SocialIconLink Icon={LinkedinLogo} url={data.linkedinUrl!} ariaLabel="LinkedIn" data-id="element-412" />}
      {facebookConnected && <SocialIconLink Icon={FacebookLogo} url={data.facebookUrl!} ariaLabel="Facebook" data-id="element-413" />}
      {websiteConnected && <SocialIconLink Icon={GlobeIcon} url={data.websiteUrl!} ariaLabel="Website" data-id="element-414" />}
    </>;
}
// ── Sub-components ──────────────────────────────────────────────────────
function SocialIconButton({
  Icon,
  connected,
  url,
  connectedLabel,
  disconnectedLabel,
  onConnect
}: {
  Icon: ComponentType<any>;
  connected: boolean;
  url?: string;
  connectedLabel: string;
  disconnectedLabel: string;
  onConnect: () => void;
}) {
  return <button type="button" onClick={() => {
    if (connected && url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      onConnect();
    }
  }} className="group relative cursor-pointer" aria-label={connected ? connectedLabel : disconnectedLabel} data-id="element-415">
      <Icon size={24} weight="fill" className={cn('transition-colors', connected ? 'text-light-2 group-hover:text-light-1' : 'text-light-4 group-hover:text-light-2')} data-id="element-416" />
      <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-dark-3 text-xs text-light-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity" data-id="element-417">
        {connected ? connectedLabel : disconnectedLabel}
      </span>
    </button>;
}
function SocialIconLink({
  Icon,
  url,
  ariaLabel
}: {
  Icon: ComponentType<any>;
  url: string;
  ariaLabel: string;
}) {
  return <a href={url} target="_blank" rel="noopener noreferrer" className="group relative cursor-pointer" aria-label={ariaLabel} data-id="element-418">
      <Icon size={24} weight="fill" className="text-light-3 hover:text-light-1 transition-colors" data-id="element-419" />
      <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-dark-3 text-xs text-light-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity" data-id="element-420">
        {ariaLabel}
      </span>
    </a>;
}
function TrustBadge({
  Icon,
  state,
  label,
  actionLabel,
  mode,
  onClick
}: {
  Icon: ComponentType<any>;
  state: VerificationState;
  label: string;
  actionLabel: string;
  mode: 'owner' | 'public';
  onClick: () => void;
}) {
  if (state.status === 'verified') {
    const displayLabel = state.verifiedAt ? `${label} verificat · ${state.verifiedAt}` : `${label} verificat`;
    return <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-lime-5 border-lime-4 text-light-1 text-sm" title={displayLabel} data-id="element-421">
        <Icon size={18} className="text-lime-1" data-id="element-422" />
        <span className="hidden sm:inline" data-id="element-423">{displayLabel}</span>
      </div>;
  }
  if (state.status === 'pending') {
    return <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-amber-500/10 border-amber-500/30 text-amber-300 text-sm" title={`${label} în review`} data-id="element-424">
        <Clock size={18} className="text-amber-400" data-id="element-425" />
        <span className="hidden sm:inline" data-id="element-426">{label} · în review</span>
      </div>;
  }
  if (state.status === 'rejected') {
    return mode === 'owner' ? <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 text-sm transition-colors cursor-pointer" title={`${label} respins. Vezi motivul.`} data-id="element-427">
        <WarningCircle size={18} className="text-red-400" data-id="element-428" />
        <span className="hidden sm:inline" data-id="element-429">{label} · respins</span>
        <span className="sm:hidden text-xs font-medium" data-id="element-430">Respins</span>
      </button> : <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-red-500/10 border-red-500/30 text-red-400 text-sm" title={`${label} respins`} data-id="element-431">
        <WarningCircle size={18} className="text-red-400" data-id="element-432" />
        <span className="hidden sm:inline" data-id="element-433">{label} · respins</span>
      </div>;
  }
  if (state.status === 'expired') {
    return mode === 'owner' ? <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-orange-500/10 border-orange-500/30 text-orange-400 hover:bg-orange-500/20 text-sm transition-colors cursor-pointer" title={`${label} expirat. Re-verifică.`} data-id="element-434">
        <XCircle size={18} className="text-orange-400" data-id="element-435" />
        <span className="hidden sm:inline" data-id="element-436">{label} · expirat</span>
        <span className="sm:hidden text-xs font-medium" data-id="element-437">Expirat</span>
      </button> : <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-orange-500/10 border-orange-500/30 text-orange-400 text-sm" title={`${label} expirat`} data-id="element-438">
        <XCircle size={18} className="text-orange-400" data-id="element-439" />
        <span className="hidden sm:inline" data-id="element-440">{label} · expirat</span>
      </div>;
  }
  if (mode === 'owner') {
    return <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-dark-3 border-dark-4 text-light-3 hover:text-light-1 hover:border-light-4 text-sm transition-colors cursor-pointer" title={actionLabel} data-id="element-441">
        <Icon size={18} className="text-light-4" data-id="element-442" />
        <span className="hidden sm:inline" data-id="element-443">{actionLabel}</span>
        <span className="sm:hidden text-xs font-medium" data-id="element-444">Verifică</span>
      </button>;
  }
  return <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-dark-3 border-dark-4 text-light-3 text-sm" title={`${label} neverificat`} data-id="element-445">
      <Icon size={18} className="text-light-4" data-id="element-446" />
      <span className="hidden sm:inline" data-id="element-447">{label} neverificat</span>
    </div>;
}